import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, categoryContent } from '@/mocks/content';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const content = slug ? categoryContent[slug] || [] : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-heading text-foreground-900 mb-3">频道未找到</h2>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bytvstream.github.io' },
      { '@type': 'ListItem', position: 2, name: `${category.name}频道`, item: `https://bytvstream.github.io/category/${slug}` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{category.name}频道</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              {category.name}频道
            </h1>
            <p className="text-sm text-foreground-500">{category.description}</p>
          </div>

          <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <div className={`w-5 h-5 flex items-center justify-center`}>
                  <i className={`${cat.icon} text-sm`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.map((item) => (
              <Link
                key={item.id}
                to={`/article/${item.id}`}
                className="group cursor-pointer bg-background-100 rounded-xl overflow-hidden hover:shadow-sm transition-all duration-300"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded bg-black/60 text-white whitespace-nowrap">
                    <i className="ri-star-fill text-primary-400 text-xs mr-1"></i>
                    {item.rating}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {item.duration}
                    </span>
                    <span>{item.views} 次播放</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}